// Libraries
import { MinusIcon } from '@chakra-ui/icons';
import { Center, Flex } from '@chakra-ui/react';
import {
  GroupBase,
  OptionProps,
  PlaceholderProps,
  SelectComponentsConfig,
  chakraComponents,
} from 'chakra-react-select';
// Components
import { RoundedCheckIcon } from '@atoms/index';
// Types
import { BasicOption, SelectKeys } from './FixedDropdown.type';

/**
 * Renders a placeholder component for a Select input.
 *
 * @component
 * @param {PlaceholderProps} props - The props for the Placeholder component.
 * @returns {ReactElement} - The rendered Placeholder component.
 * @template {BasicOption} OptionType - The type of the options in the Select input.
 * @template {boolean} IsMulti - A boolean indicating whether the Select input allows multiple selections.
 */
const Placeholder = <OptionType extends BasicOption, IsMulti extends boolean>(
  props: PlaceholderProps<OptionType, IsMulti, GroupBase<OptionType>>
) => {
  return <chakraComponents.Placeholder {...props}>{''}</chakraComponents.Placeholder>;
};

/**
 * Renders an option component for a Select input.
 *
 * @component
 * @param {OptionProps} props - The props for the Option component.
 * @returns {ReactElement} The rendered Option component.
 * @template {BasicOption} OptionType - The type of the options in the Select input.
 * @template {boolean} IsMulti - A boolean indicating whether the Select input allows multiple selections.
 */
const Option = <OptionType extends BasicOption, IsMulti extends boolean>({
  children,
  ...rest
}: OptionProps<OptionType, IsMulti, GroupBase<OptionType>>) => {
  const isAllOption = rest.data.value === SelectKeys.SELECT_ALL;
  const selectedCount = (rest.selectProps.value as OptionType[])?.length;
  const optionsCount = rest.options.length;
  const isAnySelected = selectedCount >= 1;
  let color = 'transparent';
  let boxStyles = {
    bg: 'transparent',
    border: 'greyCheck',
  };

  if (rest.isSelected || (isAllOption && isAnySelected)) {
    color = 'white';
    boxStyles = {
      bg: 'submitScheme.500',
      border: 'check',
    };
  }

  return (
    <chakraComponents.Option {...rest}>
      <Flex flex={1} align='center' justify='space-between'>
        {children}
        <Center w={4} h={4} borderRadius='base' {...boxStyles}>
          {isAllOption && selectedCount !== optionsCount ? (
            <MinusIcon w={2.5} h={2.5} color={color} />
          ) : (
            <RoundedCheckIcon w={2.5} h={2.5} color={color} />
          )}
        </Center>
      </Flex>
    </chakraComponents.Option>
  );
};

/**
 * Returns a partial configuration object for customizing the components of a Select input.
 *
 * @function
 * @returns {SelectComponentsConfig} The partial configuration object for Select components.
 * @template {BasicOption} OptionType - The type of the options in the Select input.
 * @template {boolean} IsMulti - A boolean indicating whether the Select input allows multiple selections.
 */
export const selectComponents = <OptionType extends BasicOption, IsMulti extends boolean>(): Partial<
  SelectComponentsConfig<OptionType, IsMulti, GroupBase<OptionType>>
> => ({
  Option,
  Placeholder,
  DropdownIndicator: null,
  IndicatorSeparator: null,
});
